"use client";

import { Children, Fragment, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import styles from "./AppSelect.module.css";

function textOf(node) {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (isValidElement(node)) return textOf(node.props.children);
  return Children.toArray(node).map(textOf).join("");
}

function optionsOf(children, group = "", groupDisabled = false) {
  return Children.toArray(children).flatMap(child => {
    if (!isValidElement(child)) return [];
    if (child.type === Fragment) return optionsOf(child.props.children, group, groupDisabled);
    if (child.type === "optgroup") return optionsOf(child.props.children, child.props.label, child.props.disabled);
    if (child.type !== "option") return [];
    return [{value:String(child.props.value ?? textOf(child.props.children)), label:child.props.children, text:textOf(child.props.children), disabled:groupDisabled || child.props.disabled, group}];
  });
}

export default function AppSelect({ children, value, defaultValue, onChange, className = "", disabled = false, id, ...props }) {
  const options = useMemo(() => optionsOf(children), [children]);
  const internalId = useId();
  const listId = `${internalId}-options`;
  const [internalValue, setInternalValue] = useState(String(defaultValue ?? options[0]?.value ?? ""));
  const selectedValue = value === undefined ? internalValue : String(value ?? "");
  const selectedIndex = options.findIndex(option => option.value === selectedValue);
  const [open, setOpen] = useState(false);
  const [portalHost, setPortalHost] = useState(null);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState({});
  const [invalid, setInvalid] = useState(false);
  const trigger = useRef(null);
  const native = useRef(null);
  const menu = useRef(null);
  const typeahead = useRef({text:"",time:0});
  const resetValueRef = useRef("");
  useLayoutEffect(() => {
    resetValueRef.current = value === undefined ? String(defaultValue ?? options[0]?.value ?? "") : selectedValue;
  }, [defaultValue, options, selectedValue, value]);

  const move = step => {
    let index = active;
    for(let attempts = 0; attempts < options.length; attempts += 1) {
      index = (index + step + options.length) % options.length;
      if(!options[index].disabled) {setActive(index);break;}
    }
  };
  const show = () => {
    if(disabled) return;
    setPortalHost(trigger.current?.closest("dialog[open]") || document.body);
    const index = selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : options.findIndex(option => !option.disabled);
    setActive(Math.max(index,0));
    setOpen(true);
  };
  const choose = index => {
    const option = options[index];
    if(!option || option.disabled) return;
    if(value === undefined) setInternalValue(option.value);
    setInvalid(false);
    // Keep a real select as the form value and dispatch its standard change event.
    // Existing callers continue to receive React's normal target/currentTarget.
    native.current.value = option.value;
    native.current.dispatchEvent(new Event("change", {bubbles:true}));
    setOpen(false);
    trigger.current.focus();
  };

  useEffect(() => {
    const form = native.current?.form;
    const reset = event => {
      // The browser resets the hidden select after the reset event. Reconcile
      // it afterwards so FormData and the visible label keep the same value.
      setTimeout(() => {
        if(event.defaultPrevented || !native.current) return;
        const resetValue = resetValueRef.current;
        if(value === undefined) setInternalValue(resetValue);
        native.current.value = resetValue;
        setInvalid(false);
        setOpen(false);
      }, 0);
    };
    form?.addEventListener("reset", reset);
    return () => form?.removeEventListener("reset", reset);
  }, [defaultValue, options, value]);

  useEffect(() => {
    if(!open) return;
    const reposition = () => {
      const rect = trigger.current.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const upward = below < 180 && above > below;
      setPosition({position:"fixed",left:Math.max(8,Math.min(rect.left, window.innerWidth - rect.width - 8)),width:Math.min(rect.width,window.innerWidth-16),maxHeight:Math.min(288,Math.max(80,upward ? above : below)),...(upward ? {bottom:window.innerHeight - rect.top + 6} : {top:rect.bottom+6}),direction:getComputedStyle(trigger.current).direction});
    };
    const outside = event => {
      if(!trigger.current?.contains(event.target) && !menu.current?.contains(event.target)) setOpen(false);
    };
    reposition();
    document.addEventListener("pointerdown",outside);
    document.addEventListener("focusin",outside);
    window.addEventListener("resize",reposition);
    window.addEventListener("scroll",reposition,true);
    return () => {
      document.removeEventListener("pointerdown",outside);
      document.removeEventListener("focusin",outside);
      window.removeEventListener("resize",reposition);
      window.removeEventListener("scroll",reposition,true);
    };
  }, [open]);

  useEffect(() => {
    if(open) menu.current?.querySelector(`[data-option-index="${active}"]`)?.scrollIntoView({block:"nearest"});
  }, [active,open]);

  const keyDown = event => {
    if(["ArrowDown","ArrowUp","Home","End","Enter"," ","Escape"].includes(event.key)) event.preventDefault();
    if(event.key === "Tab" || event.key === "Escape") {setOpen(false);return;}
    if(event.key === "ArrowDown" || event.key === "ArrowUp") {
      if(!open) show(); else move(event.key === "ArrowDown" ? 1 : -1);
    } else if(event.key === "Home") {if(!open) show();setActive(Math.max(0,options.findIndex(option => !option.disabled)));}
    else if(event.key === "End") {if(!open) show();setActive(options.findLastIndex(option => !option.disabled));}
    else if(event.key === "Enter" || event.key === " ") {if(open) choose(active);else show();}
    else if(event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      const text = (now - typeahead.current.time < 800 ? typeahead.current.text : "") + event.key;
      typeahead.current = {text,time:now};
      const match = options.findIndex(option => !option.disabled && option.text.toLocaleLowerCase().startsWith(text.toLocaleLowerCase()));
      if(match >= 0) {if(!open) show();setActive(match);}
    }
  };

  return <>
    <button ref={trigger} type="button" id={id} role="combobox" className={`${styles.trigger} ${className}`} disabled={disabled} aria-expanded={open && !disabled} aria-controls={open ? listId : undefined} aria-haspopup="listbox" aria-activedescendant={open ? `${listId}-${active}` : undefined} aria-label={props["aria-label"]} aria-labelledby={props["aria-labelledby"]} aria-describedby={props["aria-describedby"]} aria-required={props.required || undefined} aria-invalid={props["aria-invalid"] || invalid || undefined} title={props.title} onClick={() => open ? setOpen(false) : show()} onKeyDown={keyDown}>
      <span className={styles.label} dir="auto">{options[selectedIndex]?.label ?? props.placeholder ?? "انتخاب کنید"}</span><ChevronDown size={16} className={open ? styles.rotated : ""}/>
    </button>
    <select ref={native} {...props} disabled={disabled} tabIndex={-1} aria-hidden="true" className={styles.native} value={selectedValue} onChange={onChange} onInvalid={event => {event.preventDefault();setInvalid(true);trigger.current.focus();}}>{children}</select>
    {open && !disabled && createPortal(<div ref={menu} id={listId} role="listbox" className={styles.menu} style={position} aria-label={props["aria-label"]} onPointerDown={event => event.preventDefault()}>{options.length ? options.map((option,index) => <Fragment key={`${option.value}-${index}`}>
      {option.group && option.group !== options[index-1]?.group && <div className={styles.group}>{option.group}</div>}
      <div role="option" id={`${listId}-${index}`} data-option-index={index} aria-selected={option.value === selectedValue} aria-disabled={option.disabled || undefined} className={`${styles.option} ${active === index ? styles.active : ""}`} onPointerMove={() => !option.disabled && setActive(index)} onClick={() => choose(index)}><span dir="auto">{option.label}</span>{option.value === selectedValue && <Check size={16}/>}</div>
    </Fragment>) : <p className={styles.empty}>گزینه‌ای موجود نیست</p>}</div>,portalHost)}
  </>;
}
